import { createApp, reactive, type App, type Component, type ComponentPublicInstance, type Plugin } from 'vue';
import { createPinia, type Pinia } from 'pinia';

import type { AppStudioPageProps } from './app-studio-page-props';
import { AppStudioBootstrapPlugin } from '../Components/app-studio-bootstrap-plugin';
import SoftoolsGlobalStore from '../Store/softools-global-store';

export interface MountAppStudioPageOptions<
    TModel = unknown,
    TOptions = Record<string, unknown>,
    TPageProps extends AppStudioPageProps<TModel, TOptions> = AppStudioPageProps<TModel, TOptions>
> {
    root: string | Element;
    propsElement: string | HTMLScriptElement;
    component: Component;
    plugins?: Plugin[];
    pinia?: Pinia;
    mapComponentProps?: (props: TPageProps) => Record<string, unknown>;
    onError?: (error: unknown) => void;
}

export interface MountedAppStudioPage<
    TModel = unknown,
    TOptions = Record<string, unknown>,
    TPageProps extends AppStudioPageProps<TModel, TOptions> = AppStudioPageProps<TModel, TOptions>
> {
    app: App<Element>;
    pinia: Pinia;
    props: TPageProps;
    vm: ComponentPublicInstance;
}

let fallbackPinia: Pinia | undefined;

export function getAppStudioPinia(): Pinia {
    if (typeof window !== 'undefined' && window.softools) {
        window.softools.pinia ??= createPinia();
        return window.softools.pinia;
    }

    fallbackPinia ??= createPinia();
    return fallbackPinia;
}

export function readAppStudioPageProps<
    TModel = unknown,
    TOptions = Record<string, unknown>,
    TPageProps extends AppStudioPageProps<TModel, TOptions> = AppStudioPageProps<TModel, TOptions>
>(propsElement: string | HTMLScriptElement): TPageProps {
    const element = resolveElement<HTMLScriptElement>(propsElement, 'propsElement');
    const json = element.textContent?.trim();

    if (!json) {
        throw new Error(`App Studio page props element "${describeElement(propsElement)}" is empty.`);
    }

    return JSON.parse(json) as TPageProps;
}

export function mountAppStudioPage<
    TModel = unknown,
    TOptions = Record<string, unknown>,
    TPageProps extends AppStudioPageProps<TModel, TOptions> = AppStudioPageProps<TModel, TOptions>
>(options: MountAppStudioPageOptions<TModel, TOptions, TPageProps>): MountedAppStudioPage<TModel, TOptions, TPageProps> {
    try {
        const root = resolveElement<Element>(options.root, 'root');
        const props = readAppStudioPageProps<TModel, TOptions, TPageProps>(options.propsElement);
        const pinia = options.pinia ?? getAppStudioPinia();
        // createApp root props are shallow. Make the server-rendered JSON deeply reactive so
        // nested v-model bindings and watchers retain the behavior of the legacy Vue data object.
        const componentProps = reactive(options.mapComponentProps?.(props) ?? { pageProps: props });
        const app = createApp(options.component, componentProps);

        window.softools?.configureVueApp?.(app);
        SoftoolsGlobalStore.installPinia(app, pinia);
        app.use(AppStudioBootstrapPlugin);
        for (const plugin of options.plugins ?? []) {
            app.use(plugin);
        }

        return {
            app,
            pinia,
            props,
            vm: app.mount(root)
        };
    } catch (error) {
        options.onError?.(error);
        console.error('Failed to mount App Studio Vue page.', error);
        throw error;
    }
}

function resolveElement<TElement extends Element>(elementOrSelector: string | TElement, optionName: string): TElement {
    if (typeof elementOrSelector !== 'string') {
        return elementOrSelector;
    }

    const element = document.querySelector<TElement>(elementOrSelector);
    if (!element) {
        throw new Error(`App Studio Vue mount option "${optionName}" did not match "${elementOrSelector}".`);
    }

    return element;
}

function describeElement(elementOrSelector: string | Element): string {
    return typeof elementOrSelector === 'string' ? elementOrSelector : `#${elementOrSelector.id}`;
}
